// API service for handling authentication and product data

const API_BASE_URL = 'https://et-pos.store';
const ROLE_API_BASE_URL = 'https://et-pos.store';

// Token storage keys
const TOKEN_KEY = 'etpos_token';
const TOKEN_EXPIRY_KEY = 'etpos_token_expiry';

class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL;
    this.roleBaseURL = ROLE_API_BASE_URL;
  }

  // Login with username and password using OAuth token endpoint
  async login(username, password) {
    try {
      const response = await fetch(`${this.baseURL}/oauth/token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          grant_type: 'password',
          client_id: '32',
          client_secret: 'MjSuv4nquj67caVWanVpDX5YjrTdiXBVuAooJ13r',
          username: username,
          password: password
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Login failed:', errorText);

        if (response.status === 401) {
          throw new Error('Incorrect Username, Password, Or user not active.');
        } else if (response.status === 500) {
          throw new Error('Server error. Please contact administrator.');
        } else {
          throw new Error('Incorrect Username, Password, Or user not active.');
        }
      }

      const data = await response.json();

      if (data.access_token) {
        // Store token with expiry
        const expiryTime = new Date();
        expiryTime.setHours(expiryTime.getHours() + 24); // 24 hour expiry

        localStorage.setItem(TOKEN_KEY, data.access_token);
        localStorage.setItem(TOKEN_EXPIRY_KEY, expiryTime.toISOString());

        return { success: true, token: data.access_token };
      } else {
        throw new Error('No access token received from server');
      }
    } catch (error) {
      console.error('Error during login:', error);
      throw error;
    }
  }

  // Get authentication token
  async getAuthToken() {
    // Check if we have a valid token stored
    const storedToken = localStorage.getItem(TOKEN_KEY);
    const tokenExpiry = localStorage.getItem(TOKEN_EXPIRY_KEY);

    if (storedToken && tokenExpiry && new Date(tokenExpiry) > new Date()) {
      return storedToken;
    }

    // If no valid token, user needs to login
    throw new Error('No valid authentication token. Please login.');
  }

  // Get user details (business_id, location_id, etc.)
  async getUserDetails() {
    console.log('🔐 RBAC DEBUG - Fetching user details...');
    try {
      const token = await this.getAuthToken();
      console.log('🔐 RBAC DEBUG - Token obtained for user details:', token ? 'success' : 'failed');

      if (!token) {
        console.error('🔐 RBAC DEBUG - No token available, cannot fetch user details');
        throw new Error('No authentication token available');
      }

      const response = await fetch(`${this.baseURL}/connector/api/user/loggedin`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('🔐 RBAC DEBUG - User details response status:', response.status);
      console.log('🔐 RBAC DEBUG - User details response ok:', response.ok);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('🔐 RBAC DEBUG - User details error response:', errorText);
        throw new Error(`Failed to fetch user details: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      console.log('🔐 RBAC DEBUG - User details response data:', data);

      // Extract actual user data from nested response
      const userData = data.data || data;
      console.log('🔐 RBAC DEBUG - Extracted user data:', userData);
      console.log('🔐 RBAC DEBUG - User roles:', userData.roles);
      console.log('🔐 RBAC DEBUG - User all_permissions:', userData.all_permissions);
      console.log('🔐 RBAC DEBUG - User business_id:', userData.business_id);
      console.log('🔐 RBAC DEBUG - User data keys:', Object.keys(userData));

      if (userData.all_permissions && Array.isArray(userData.all_permissions)) {
        console.log('🔐 RBAC DEBUG - Found direct permissions:', userData.all_permissions);
        console.log('🔐 RBAC DEBUG - Permission count:', userData.all_permissions.length);
      } else if (userData.roles && userData.roles.length > 0) {
        console.log('🔐 RBAC DEBUG - Primary role:', userData.roles[0]);
        console.log('🔐 RBAC DEBUG - Role permissions:', userData.roles[0].permissions);
      } else {
        console.log('🔐 RBAC DEBUG - No roles or all_permissions found in user data');
        console.log('🔐 RBAC DEBUG - Available user fields:', Object.keys(userData));
      }

      console.log('🔐 RBAC DEBUG - getUserDetails completed successfully');
      return data;
    } catch (error) {
      console.error('🔐 RBAC DEBUG - Error fetching user details:', error);
      console.log('🔐 RBAC DEBUG - Error details:', error.message, error.stack);
      throw error;
    }
  }

  // Get profit/loss report data
  async getProfitLossReport(params = {}) {
    try {
      const token = await this.getAuthToken();

      const queryParams = new URLSearchParams();
      if (params.start_date) queryParams.append('start_date', params.start_date);
      if (params.end_date) queryParams.append('end_date', params.end_date);
      if (params.location_id) queryParams.append('location_id', params.location_id);

      const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
      const response = await fetch(`${this.baseURL}/connector/api/profit-loss-report${queryString}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch profit/loss report: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching profit/loss report:', error);
      throw error;
    }
  }

  // Get sales last 30 days data
  async getSalesLast30Days() {
    try {
      const token = await this.getAuthToken();

      const response = await fetch(`${this.baseURL}/api/dashboard/sales-last-30-days`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch sales last 30 days: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching sales last 30 days:', error);
      throw error;
    }
  }

  // Create a new product — POST /connector/api/product
  async createProduct(productData) {
    try {
      const token = await this.getAuthToken();

      // Initialize session by calling getUserDetails first
      // This ensures the API server has a valid session before creating the product
      try {
        await this.getUserDetails();
        console.log('Session initialized successfully');
      } catch (sessionError) {
        console.warn('Failed to initialize session, continuing anyway:', sessionError);
      }

      const url = `${this.baseURL}/connector/api/product`;

      console.log('Creating product with data:', productData);

      // Use FormData so we can attach image/brochure files alongside JSON-like fields
      const formData = new FormData();

      // ── Core fields ────────────────────────────────────────────────────────
      const appendIfSet = (key, value) => {
        if (value !== null && value !== undefined && value !== '') {
          formData.append(key, value);
        }
      };

      appendIfSet('name', productData.name);
      appendIfSet('type', productData.type || 'single');
      appendIfSet('sku', productData.sku);
      appendIfSet('barcode_type', productData.barcode_type);
      appendIfSet('unit_id', productData.unit_id);
      appendIfSet('brand_id', productData.brand_id);
      appendIfSet('category_id', productData.category_id);
      appendIfSet('sub_category_id', productData.sub_category_id);
      appendIfSet('tax', productData.tax);
      appendIfSet('tax_type', productData.tax_type);
      appendIfSet('enable_stock', productData.enable_stock);
      appendIfSet('not_for_selling', productData.not_for_selling);
      appendIfSet('alert_quantity', productData.alert_quantity);
      appendIfSet('product_description', productData.product_description);
      appendIfSet('weight', productData.weight);
      appendIfSet('warranty_id', productData.warranty_id);
      appendIfSet('sub_unit_ids', productData.sub_unit_ids);
      appendIfSet('preparation_time_in_minutes', productData.preparation_time_in_minutes);

      // ── Pricing (single product) ────────────────────────────────────────────
      appendIfSet('single_dpp', productData.single_dpp);
      appendIfSet('single_dpp_inc_tax', productData.single_dpp_inc_tax);
      appendIfSet('profit_percent', productData.profit_percent);
      appendIfSet('single_dsp', productData.single_dsp);
      appendIfSet('single_dsp_inc_tax', productData.single_dsp_inc_tax);

      // ── product_locations: array → FormData array notation ────────────────
      if (Array.isArray(productData.product_locations)) {
        productData.product_locations.forEach((locId) => {
          formData.append('product_locations[]', locId);
        });
      }

      // ── product_racks: object → FormData nested notation ──────────────────
      if (productData.product_racks && typeof productData.product_racks === 'object') {
        Object.entries(productData.product_racks).forEach(([locationId, rack]) => {
          formData.append(`product_racks[${locationId}][rack]`, rack.rack || '');
          formData.append(`product_racks[${locationId}][row]`, rack.row || '');
          formData.append(`product_racks[${locationId}][position]`, rack.position || '');
        });
      }

      // ── product_variation: variable product variations ────────────────────
      if (productData.type === 'variable' && Array.isArray(productData.product_variation)) {
        productData.product_variation.forEach((variation, vi) => {
          formData.append(`product_variation[${vi}][name]`, variation.name || '');
          if (Array.isArray(variation.variations)) {
            variation.variations.forEach((v, vvi) => {
              formData.append(`product_variation[${vi}][variations][${vvi}][name]`, v.name || '');
              formData.append(`product_variation[${vi}][variations][${vvi}][default_purchase_price]`, v.default_purchase_price || 0);
              formData.append(`product_variation[${vi}][variations][${vvi}][dpp_inc_tax]`, v.dpp_inc_tax || 0);
              formData.append(`product_variation[${vi}][variations][${vvi}][profit_percent]`, v.profit_percent || 0);
              formData.append(`product_variation[${vi}][variations][${vvi}][default_sell_price]`, v.default_sell_price || 0);
              formData.append(`product_variation[${vi}][variations][${vvi}][sell_price_inc_tax]`, v.sell_price_inc_tax || 0);
            });
          }
        });
      }

      // ── Files ──────────────────────────────────────────────────────────────
      if (productData.image) formData.append('image', productData.image);
      if (productData.brochure) formData.append('brochure', productData.brochure);

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
          // Do NOT set Content-Type — browser sets it with boundary for FormData
        },
        body: formData
      });

      console.log('Product creation response status:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Product creation error:', errorText);
        let errMsg = `Failed to create product: ${response.status}`;
        try {
          const errJson = JSON.parse(errorText);
          errMsg = errJson.message || errMsg;
        } catch (_) { /* not JSON */ }
        throw new Error(errMsg);
      }

      const responseText = await response.text();
      console.log('Product creation response:', responseText);

      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        throw new Error(`API returned non-JSON response: ${responseText.substring(0, 200)}`);
      }

      return data;
    } catch (error) {
      console.error('Error creating product:', error);
      throw error;
    }
  }

  // Update an existing product
  async updateProduct(productId, productData) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/product/${productId}`;

      console.log('Updating product with data:', productData);

      const formData = new FormData();

      Object.keys(productData).forEach(key => {
        if (key === 'image' || key === 'brochure') {
          if (productData[key]) {
            formData.append(key, productData[key]);
          }
        } else if (key === 'description') {
          if (productData[key]) {
            formData.append(key, productData[key]);
          }
        } else {
          formData.append(key, productData[key]);
        }
      });

      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Product update error:', errorText);
        throw new Error(`Failed to update product: ${response.status} - ${errorText}`);
      }

      const responseText = await response.text();
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error('Failed to parse product update response:', parseError);
        throw new Error(`API returned non-JSON response: ${responseText.substring(0, 200)}...`);
      }

      return data;
    } catch (error) {
      console.error('Error updating product:', error);
      throw error;
    }
  }



  // Get payment accounts from API
  async getPaymentAccounts() {
    try {
      const token = await this.getAuthToken();

      const url = `${this.baseURL}/connector/api/payment-accounts`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch payment accounts: ${response.status}`);
      }

      const data = await response.json();
      console.log('Raw payment accounts from API:', data);

      // Filter duplicates at API level
      const accounts = data.data || data || [];
      const uniqueAccounts = [];
      const seenIds = new Set();
      const seenNames = new Set();

      accounts.forEach(account => {
        const accountId = account.id || account.account_id;
        const accountName = (account.name || account.account_name || account.display_name || '').toLowerCase().trim();

        // Only add if we haven't seen this ID or name before
        if (!seenIds.has(accountId) && !seenNames.has(accountName)) {
          seenIds.add(accountId);
          seenNames.add(accountName);
          uniqueAccounts.push(account);
        }
      });

      console.log('Payment accounts after API-level deduplication:', uniqueAccounts);

      // Return the same structure but with deduplicated accounts
      if (data.data) {
        return { ...data, data: uniqueAccounts };
      } else {
        return uniqueAccounts;
      }
    } catch (error) {
      console.error('Error fetching payment accounts:', error);
      throw error;
    }
  }

  // Get contacts from API with optional search
  async getContacts(searchTerm = {}) {
    let paramsInit = {};
    try {
      const token = await this.getAuthToken();

      // Handle legacy string argument
      if (typeof searchTerm === 'string') {
        paramsInit = { name: searchTerm, per_page: searchTerm.trim() ? '50' : '100' };
      } else {
        paramsInit = { ...searchTerm }; // It's an object
      }

      const params = new URLSearchParams();

      if (paramsInit.name) params.append('name', paramsInit.name);
      if (paramsInit.type) params.append('type', paramsInit.type);
      if (paramsInit.per_page) params.append('per_page', paramsInit.per_page);
      if (paramsInit.page) params.append('page', paramsInit.page);

      // Default per_page if not set
      if (!params.has('per_page')) params.append('per_page', '100');

      const url = `${this.baseURL}/connector/api/contactapi?${params.toString()}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch contacts: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching contacts:', error);
      throw error;
    }
  }

  // Create a new contact (Supplier or Customer)
  async createContact(contactData) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/contactapi`;

      console.log('Creating contact with data:', contactData);

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(contactData)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Contact creation error:', errorText);
        let errMsg = `Failed to create contact: ${response.status}`;
        try {
          const errJson = JSON.parse(errorText);
          errMsg = errJson.message || errMsg;
        } catch (_) { /* not JSON */ }
        throw new Error(errMsg);
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating contact:', error);
      throw error;
    }
  }

  // Update an existing contact
  async updateContact(contactId, contactData) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/contactapi/${contactId}`;

      console.log(`Updating contact ${contactId} with data:`, contactData);

      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(contactData)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Contact update error:', errorText);
        let errMsg = `Failed to update contact: ${response.status}`;
        try {
          const errJson = JSON.parse(errorText);
          errMsg = errJson.message || errMsg;
        } catch (_) { /* not JSON */ }
        throw new Error(errMsg);
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating contact:', error);
      throw error;
    }
  }

  // Get customer by ID to get full details including balance
  async getCustomerById(customerId) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/contactapi/${customerId}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch customer details: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching customer details:', error);
      throw error;
    }
  }

  // Get tax rates from API
  async getTaxRates() {
    try {
      const token = await this.getAuthToken();

      const url = `${this.baseURL}/connector/api/tax`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch tax rates: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching tax rates:', error);
      throw error;
    }
  }

  // Get invoice schemes
  async getInvoiceSchemes() {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/api/invoice-schema`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch invoice schemes: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching invoice schemes:', error);
      throw error;
    }
  }

  // Save sell data to API using new endpoint
  async saveSell(sellData) {
    try {
      const token = await this.getAuthToken();

      const url = `${this.baseURL}/api/sales1`;

      console.log('Making API call to:', url);
      console.log('Request data:', sellData);

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(sellData)
      });

      console.log('Response status:', response.status);
      console.log('Response headers:', response.headers);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response:', errorText);

        // Try to parse error as JSON for better error messages
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch (e) {
          // If not JSON, use raw text
        }

        if (response.status === 401) {
          throw new Error('Authentication failed. Please check your credentials.');
        } else if (response.status === 422) {
          // Validation errors
          if (errorData && errorData.messages) {
            const messages = Object.entries(errorData.messages)
              .map(([field, message]) => `${field}: ${Array.isArray(message) ? message.join(', ') : message}`)
              .join('; ');
            throw new Error(`Validation failed: ${messages}`);
          }
          throw new Error(`Validation failed: ${errorText}`);
        } else if (response.status === 400) {
          throw new Error('Invalid data format. Please check your form data.');
        } else if (response.status === 500) {
          throw new Error('Server error. Please try again later.');
        } else {
          throw new Error(`Failed to save sell: ${response.status} - ${errorText}`);
        }
      }

      const responseText = await response.text();
      console.log('Raw response text:', responseText);

      let data;
      try {
        data = JSON.parse(responseText);
        console.log('Parsed JSON response:', data);
      } catch (parseError) {
        console.error('Failed to parse JSON response:', parseError);
        console.log('Response was not valid JSON:', responseText);
        throw new Error(`API returned non-JSON response: ${responseText.substring(0, 200)}...`);
      }

      // Check for different possible response formats
      if (!data) {
        throw new Error('API returned empty response');
      }

      // Log the actual response type and content
      console.log('Response type:', typeof data);
      console.log('Is array:', Array.isArray(data));
      console.log('Response constructor:', data.constructor.name);
      console.log('Response value:', data);

      // Handle array responses (API returns list of sales)
      if (Array.isArray(data)) {
        console.log(`API returned array with ${data.length} items`);

        if (data.length === 0) {
          console.log('API returned empty array - treating as success');
          return { success: true, message: 'Sale created successfully' };
        }

        // API returned array of sales - get the most recent one (last item or by date)
        console.log('Extracting newest sale from array response');

        // Sort by created_at or id to get the newest
        const sortedData = [...data].sort((a, b) => {
          // Try sorting by created_at timestamp
          if (a.created_at && b.created_at) {
            return new Date(b.created_at) - new Date(a.created_at);
          }
          // Fallback to ID (assuming higher ID = newer)
          if (a.id && b.id) {
            return b.id - a.id;
          }
          return 0;
        });

        const newestSale = sortedData[0];
        console.log('Newest sale extracted:', newestSale);

        return newestSale;
      }

      if (typeof data === 'object' && Object.keys(data).length === 0) {
        console.log('API returned empty object - treating as success');
        return { success: true, message: 'Sale created successfully' };
      }

      // Log all available fields to understand the structure
      const availableFields = Object.keys(data);
      console.log('Available response fields:', availableFields);

      // Check for various possible success indicators
      const hasId = data.id !== undefined;
      const hasSuccess = data.success !== undefined;
      const hasData = data.data !== undefined;
      const hasMessage = data.message !== undefined;
      const hasStatus = data.status !== undefined;

      console.log('Response analysis:', {
        hasId,
        hasSuccess,
        hasData,
        hasMessage,
        hasStatus,
        responseType: typeof data,
        isArray: Array.isArray(data),
        fieldCount: availableFields.length
      });

      // Accept response if it has any of these indicators OR if it's empty (treat as success)
      if (!hasId && !hasSuccess && !hasData && !hasMessage && !hasStatus && availableFields.length > 0) {
        console.log('Response has unexpected fields but not empty:', data);
        // Return the data as-is but mark it as processed
        return { ...data, processed: true };
      }

      // If completely empty, return a success response
      if (availableFields.length === 0) {
        return { success: true, message: 'Sale created successfully (empty response)' };
      }

      return data;
    } catch (error) {
      console.error('Error creating sale:', error);
      throw error;
    }
  }

  // Update existing sell data via API
  async updateSell(saleId, sellData) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/sell/${saleId}`;

      console.log('Updating sale at:', url);
      console.log('Update data:', sellData);

      // CRITICAL: Log the complete JSON payload being sent
      console.log('=== COMPLETE JSON PAYLOAD BEING SENT ===');
      console.log(JSON.stringify(sellData, null, 2));
      console.log('=== PRODUCTS ARRAY DETAIL ===');
      if (sellData.products) {
        sellData.products.forEach((product, index) => {
          console.log(`Product ${index}:`, {
            product_id: product.product_id,
            variation_id: product.variation_id,
            variation_id_is_null: product.variation_id === null,
            variation_id_is_undefined: product.variation_id === undefined,
            variation_id_type: typeof product.variation_id
          });
        });
      }
      console.log('=== END PAYLOAD DETAIL ===');

      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(sellData)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error updating sale:', errorText);
        throw new Error(`Failed to update sale: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      console.log('Update response:', data);
      return data;
    } catch (error) {
      console.error('Error updating sale:', error);
      throw error;
    }
  }

  // Update shipping status API
  async updateShippingStatus(transactionId, shippingStatus) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/update-shipping-status`;

      console.log('Updating shipping status for transaction:', transactionId, 'to:', shippingStatus);

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          transaction_id: parseInt(transactionId),
          shipping_status: shippingStatus
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error updating shipping status:', errorText);
        throw new Error(`Failed to update shipping status: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      console.log('Shipping status update response:', data);
      return data;
    } catch (error) {
      console.error('Error updating shipping status:', error);
      throw error;
    }
  }

  // Save shipping details separately
  async saveShippingDetails(saleId, shippingData) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/api/shipping-details`;

      console.log('Saving shipping details for sale:', saleId);
      console.log('Shipping data:', shippingData);

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          sale_id: saleId,
          ...shippingData
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error saving shipping details:', errorText);
        throw new Error(`Failed to save shipping details: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      console.log('Shipping details saved:', data);
      return data;
    } catch (error) {
      console.error('Error saving shipping details:', error);
      throw error;
    }
  }

  // Get products from API
  async getProducts(params = {}) {
    try {
      const token = await this.getAuthToken();

      // Handle legacy string argument
      if (typeof params === 'string') {
        params = { search: params };
      }

      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append('page', params.page);

      // Default to 25 if not specific (or use what is passed, -1 for all)
      if (params.per_page) {
        queryParams.append('per_page', params.per_page);
      } else {
        queryParams.append('per_page', '25');
      }

      if (params.search) {
        // Support multiple common search params to ensure SKU is caught by backend
        queryParams.append('name', params.search);
        queryParams.append('search', params.search);
        queryParams.append('sku', params.search);
        queryParams.append('term', params.search);
      }

      const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
      const url = `${this.baseURL}/connector/api/product${queryString}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch products: ${response.status}`);
      }

      const data = await response.json();
      console.log('Products fetched:', Array.isArray(data) ? data.length : (data.data ? data.data.length : 0));
      return data;
    } catch (error) {
      console.error('Error fetching products:', error);
      throw error;
    }
  }

  // Get single product details
  async getProduct(productId) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/product/${productId}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching product:', error);
      throw error;
    }
  }


  // Get payment methods from API
  async getPaymentMethods() {
    try {
      const token = await this.getAuthToken();

      const url = `${this.baseURL}/connector/api/payment-methods`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch payment methods: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching payment methods:', error);
      throw error;
    }
  }

  // Get sales from API
  // Get sales from API with filters
  async getSales(params = {}) {
    try {
      const token = await this.getAuthToken();

      const queryParams = new URLSearchParams();
      // Default to all records unless specified
      queryParams.append('per_page', params.per_page || '-1');

      // Include product details in the response (try multiple formats)
      queryParams.append('with[]', 'contact');
      queryParams.append('with[]', 'location');
      queryParams.append('with[]', 'product');
      queryParams.append('with[]', 'sell_lines.product');

      if (params.start_date) queryParams.append('start_date', params.start_date);
      if (params.end_date) queryParams.append('end_date', params.end_date);
      if (params.location_id && params.location_id !== 'all') queryParams.append('location_id', params.location_id);
      if (params.contact_id && params.contact_id !== 'all') queryParams.append('contact_id', params.contact_id);
      if (params.status && params.status !== 'all') queryParams.append('status', params.status);
      if (params.payment_status && params.payment_status !== 'all') queryParams.append('payment_status', params.payment_status);
      if (params.user_id && params.user_id !== 'all') queryParams.append('user_id', params.user_id);
      if (params.shipping_status && params.shipping_status !== 'all') queryParams.append('shipping_status', params.shipping_status);
      if (params.source && params.source !== 'all') queryParams.append('source', params.source);

      const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
      const url = `${this.baseURL}/connector/api/sell${queryString}`;

      console.log('Fetching sales with params:', url);

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch sales: ${response.status}`);
      }

      const data = await response.json();
      console.log('Sales fetched:', Array.isArray(data) ? data.length : (data.data ? data.data.length : 0));
      return data;
    } catch (error) {
      console.error('Error fetching sales:', error);
      throw error;
    }
  }

  // Delete a sale
  async deleteSale(saleId) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/sell/${saleId}`;

      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        let errorText = await response.text();
        try {
          const errorJson = JSON.parse(errorText);
          errorText = errorJson.message || JSON.stringify(errorJson) || errorText;
        } catch (e) { }
        throw new Error(`${response.status} - ${errorText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error deleting sale:', error);
      throw error;
    }
  }

  // Create Sell Return
  async createSellReturn(returnData) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/sell-return`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(returnData)
      });

      if (!response.ok) {
        let errorText = await response.text();
        try {
          const errorJson = JSON.parse(errorText);
          errorText = errorJson.message || JSON.stringify(errorJson) || errorText;
        } catch (e) { }
        throw new Error(`${response.status} - ${errorText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating sell return:', error);
      throw error;
    }
  }

  // Get sell returns from API with filters
  async getSellReturns(params = {}) {
    try {
      const token = await this.getAuthToken();

      const queryParams = new URLSearchParams();
      // Default to all records unless specified
      queryParams.append('per_page', params.per_page || '-1');

      if (params.start_date) queryParams.append('start_date', params.start_date);
      if (params.end_date) queryParams.append('end_date', params.end_date);
      if (params.location_id && params.location_id !== 'all') queryParams.append('location_id', params.location_id);
      if (params.contact_id && params.contact_id !== 'all') queryParams.append('contact_id', params.contact_id);
      if (params.payment_status && params.payment_status !== 'all') queryParams.append('payment_status', params.payment_status);
      if (params.user_id && params.user_id !== 'all') queryParams.append('user_id', params.user_id);
      if (params.shipping_status && params.shipping_status !== 'all') queryParams.append('shipping_status', params.shipping_status);
      if (params.source && params.source !== 'all') queryParams.append('source', params.source);

      const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
      const url = `${this.baseURL}/connector/api/list-sell-return${queryString}`;

      console.log('Fetching sell returns with params:', url);

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch sell returns: ${response.status}`);
      }

      const data = await response.json();
      console.log('Sell returns fetched:', Array.isArray(data) ? data.length : (data.data ? data.data.length : 0));
      return data;
    } catch (error) {
      console.error('Error fetching sell returns:', error);
      throw error;
    }
  }

  // Clear stored token (for logout)
  clearToken() {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(TOKEN_EXPIRY_KEY);
  }

  // Check if token is valid
  isTokenValid() {
    const tokenExpiry = localStorage.getItem(TOKEN_EXPIRY_KEY);
    return tokenExpiry && new Date(tokenExpiry) > new Date();
  }

  // Get users from API with pagination and filters
  async getUsers(params = {}) {
    try {
      const token = await this.getAuthToken();
      const queryParams = new URLSearchParams();

      // Add pagination and filter parameters
      if (params.page) queryParams.append('page', params.page);
      if (params.per_page) queryParams.append('per_page', params.per_page);
      if (params.search) queryParams.append('search', params.search);
      if (params.role_id) queryParams.append('role_id', params.role_id);
      if (params.business_id) queryParams.append('business_id', params.business_id);

      const url = `${this.baseURL}/connector/api/users?${queryParams.toString()}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error(`Failed to fetch users: ${response.status}`);
      return await response.json();
    } catch (error) {
      console.error('Error fetching users:', error);
      throw error;
    }
  }

  // Create new user
  async createUser(userData) {
    debugger; // Debug: Check user data being sent
    console.log('CREATE USER API - Input userData:', userData);
    try {
      const token = await this.getAuthToken();
      console.log('CREATE USER API - Token obtained:', token ? 'success' : 'failed');

      const requestBody = JSON.stringify(userData);
      console.log('CREATE USER API - Request body:', requestBody);

      const response = await fetch(`${this.baseURL}/connector/api/users`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: requestBody
      });

      console.log('CREATE USER API - Response status:', response.status);
      console.log('CREATE USER API - Response ok:', response.ok);

      const responseData = await response.json();
      console.log('CREATE USER API - Response data:', responseData);
      debugger; // Debug: Check API response

      if (!response.ok) {
        throw new Error(responseData.message || `Failed to create user: ${response.status}`);
      }

      return responseData;
    } catch (error) {
      console.error('CREATE USER API - Error:', error);
      debugger; // Debug: Check error details
      throw error;
    }
  }

  // Get user for editing
  async getUserForEdit(userId) {
    debugger; // Debug: Check user ID being requested
    console.log('GET USER FOR EDIT API - Input userId:', userId);
    try {
      const token = await this.getAuthToken();
      console.log('GET USER FOR EDIT API - Token obtained:', token ? 'success' : 'failed');

      const response = await fetch(`${this.baseURL}/connector/api/users/${userId}/edit`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('GET USER FOR EDIT API - Response status:', response.status);
      console.log('GET USER FOR EDIT API - Response ok:', response.ok);

      const responseData = await response.json();
      console.log('GET USER FOR EDIT API - Response data:', responseData);
      debugger; // Debug: Check API response

      if (!response.ok) {
        throw new Error(`Failed to fetch user: ${response.status}`);
      }

      return responseData;
    } catch (error) {
      console.error('GET USER FOR EDIT API - Error:', error);
      debugger; // Debug: Check error details
      throw error;
    }
  }

  // Update user
  async updateUser(userId, userData) {
    debugger; // Debug: Check user ID and data being sent
    console.log('UPDATE USER API - Input userId:', userId);
    console.log('UPDATE USER API - Input userData:', userData);
    try {
      const token = await this.getAuthToken();
      console.log('UPDATE USER API - Token obtained:', token ? 'success' : 'failed');

      const requestBody = JSON.stringify(userData);
      console.log('UPDATE USER API - Request body:', requestBody);

      const response = await fetch(`${this.baseURL}/connector/api/users/${userId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: requestBody
      });

      console.log('UPDATE USER API - Response status:', response.status);
      console.log('UPDATE USER API - Response ok:', response.ok);

      const responseData = await response.json();
      console.log('UPDATE USER API - Response data:', responseData);
      debugger; // Debug: Check API response

      if (!response.ok) {
        throw new Error(responseData.message || `Failed to update user: ${response.status}`);
      }

      return responseData;
    } catch (error) {
      console.error('UPDATE USER API - Error:', error);
      debugger; // Debug: Check error details
      throw error;
    }
  }

  // Delete user
  async deleteUser(userId) {
    debugger; // Debug: Check delete user API call
    console.log('DELETE USER API - Input userId:', userId);
    try {
      const token = await this.getAuthToken();
      const userDetails = await this.getUserDetails();
      const businessId = userDetails?.business_id || 40;

      console.log('DELETE USER API - Token obtained:', token ? 'success' : 'failed');
      console.log('DELETE USER API - Business ID:', businessId);

      // Try DELETE method with business_id parameter
      let response = await fetch(`${this.baseURL}/connector/api/users/${userId}?business_id=${businessId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('DELETE USER API - DELETE Response status:', response.status);
      console.log('DELETE USER API - DELETE Response ok:', response.ok);

      // If DELETE fails with 405, try POST with _method=DELETE and business_id
      if (!response.ok && response.status === 405) {
        console.log('DELETE USER API - DELETE not allowed, trying POST with _method=DELETE');

        response = await fetch(`${this.baseURL}/connector/api/users/${userId}?business_id=${businessId}`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/x-www-form-urlencoded'
          },
          body: new URLSearchParams({
            '_method': 'DELETE',
            'business_id': businessId.toString()
          })
        });

        console.log('DELETE USER API - POST Response status:', response.status);
        console.log('DELETE USER API - POST Response ok:', response.ok);
      }

      console.log('DELETE USER API - Final Response status:', response.status);
      console.log('DELETE USER API - Final Response ok:', response.ok);

      if (!response.ok) {
        // Handle both JSON and HTML responses
        const contentType = response.headers.get('content-type');
        let errorData;

        if (contentType && contentType.includes('application/json')) {
          errorData = await response.json();
        } else {
          // Handle HTML response (error page)
          const htmlText = await response.text();
          console.log('DELETE USER API - HTML response:', htmlText.substring(0, 200));
          errorData = { message: `Delete not supported by backend (${response.status})` };
        }

        throw new Error(errorData.message || `Failed to delete user: ${response.status}`);
      }

      const responseData = await response.json();
      console.log('DELETE USER API - Response data:', responseData);
      debugger; // Debug: Check successful delete response

      return responseData;
    } catch (error) {
      console.error('DELETE USER API - Error:', error);
      debugger; // Debug: Check error details
      throw error;
    }
  }

  // Get user roles and permissions
  async getUserRoles(businessId = null) {
    console.log('🔐 RBAC DEBUG - Fetching user roles...');
    try {
      const token = await this.getAuthToken();
      console.log('🔐 RBAC DEBUG - Token obtained for user roles:', token ? 'success' : 'failed');

      if (!token) {
        console.error('🔐 RBAC DEBUG - No token available for user roles');
        return { data: [] };
      }

      const userDetails = await this.getUserDetails();
      const userId = userDetails.data?.id || userDetails.id;
      const userBusinessId = businessId || userDetails.data?.business_id || userDetails.business_id;

      console.log('🔐 RBAC DEBUG - User ID:', userId);
      console.log('🔐 RBAC DEBUG - Business ID:', userBusinessId);

      if (!userId) {
        console.log('🔐 RBAC DEBUG - No user ID found, cannot fetch roles');
        return { data: [] };
      }

      // Try to get user-specific roles
      const url = `${this.roleBaseURL}/connector/api/users/${userId}/roles?business_id=${userBusinessId}`;
      console.log('🔐 RBAC DEBUG - User roles URL:', url);

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('🔐 RBAC DEBUG - User roles response status:', response.status);
      console.log('🔐 RBAC DEBUG - User roles response ok:', response.ok);

      if (!response.ok) {
        console.log('🔐 RBAC DEBUG - User roles API failed, falling back to general roles');
        // Fallback to general roles dropdown
        return await this.getRolesDropdown(userBusinessId);
      }

      const data = await response.json();
      console.log('🔐 RBAC DEBUG - User roles response data:', data);

      if (data.data) {
        data.data.forEach((role, index) => {
          console.log(`🔐 RBAC DEBUG - User Role ${index + 1}:`, role);
          if (role.permissions) {
            console.log(`🔐 RBAC DEBUG - Role ${index + 1} permissions:`, role.permissions);
          }
        });
      }

      console.log('🔐 RBAC DEBUG - getUserRoles completed successfully');
      return data;
    } catch (error) {
      console.error('🔐 RBAC DEBUG - Error fetching user roles:', error);
      console.log('🔐 RBAC DEBUG - Falling back to roles dropdown');
      // Fallback to roles dropdown
      try {
        const userDetails = await this.getUserDetails();
        const businessId = userDetails.data?.business_id || userDetails.business_id || 40;
        return await this.getRolesDropdown(businessId);
      } catch (fallbackError) {
        console.error('🔐 RBAC DEBUG - Fallback also failed:', fallbackError);
        return { data: [] };
      }
    }
  }

  // Get roles dropdown
  async getRolesDropdown(businessId = null) {
    console.log('🔐 RBAC DEBUG - Fetching roles dropdown...');
    try {
      const token = await this.getAuthToken();
      console.log('🔐 RBAC DEBUG - Token obtained for roles dropdown:', token ? 'success' : 'failed');

      if (!token) {
        console.error('🔐 RBAC DEBUG - No token available for roles dropdown');
        return { data: [] };
      }

      const params = new URLSearchParams();
      if (businessId) {
        params.append('business_id', businessId);
        console.log('🔐 RBAC DEBUG - Business ID for roles:', businessId);
      }

      const url = `${this.baseURL}/connector/api/roles-dropdown?${params.toString()}`;
      console.log('🔐 RBAC DEBUG - Roles dropdown URL:', url);

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('🔐 RBAC DEBUG - Roles dropdown response status:', response.status);
      console.log('🔐 RBAC DEBUG - Roles dropdown response ok:', response.ok);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('🔐 RBAC DEBUG - Roles dropdown error response:', errorText);
        throw new Error(`Failed to fetch roles dropdown: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      console.log('🔐 RBAC DEBUG - Roles dropdown response data:', data);
      console.log('🔐 RBAC DEBUG - Available roles count:', data.data ? data.data.length : (Array.isArray(data) ? data.length : 0));

      if (data.data) {
        data.data.forEach((role, index) => {
          console.log(`🔐 RBAC DEBUG - Role ${index + 1}:`, role);
        });
      }

      console.log('🔐 RBAC DEBUG - getRolesDropdown completed successfully');
      return data;
    } catch (error) {
      console.error('🔐 RBAC DEBUG - Error fetching roles dropdown:', error);
      console.log('🔐 RBAC DEBUG - Error details:', error.message, error.stack);
      throw error;
    }
  }

  // Get roles from API
  async getRoles(businessId = null) {
    try {
      const token = await this.getAuthToken();
      const params = new URLSearchParams();
      if (businessId) {
        params.append('business_id', businessId);
      }
      const url = `${this.roleBaseURL}/connector/api/roles?${params.toString()}`;
      console.log('Fetching roles from URL:', url);
      console.log('Using token:', token ? 'Token present' : 'No token');

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('Roles API response status:', response.status);
      console.log('Roles API response content-type:', response.headers.get('content-type'));

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Roles API error response:', errorText);
        throw new Error(`Failed to fetch roles: ${response.status} - ${errorText}`);
      }

      // Check if response is JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const htmlText = await response.text();
        console.error('Roles API returned HTML instead of JSON:', htmlText.substring(0, 500));
        throw new Error('API returned HTML instead of JSON. Check if the endpoint exists and is configured correctly.');
      }

      const data = await response.json();
      console.log('Roles API parsed data:', data);
      return data;
    } catch (error) {
      console.error('Error fetching roles:', error);
      throw error;
    }
  }

  // Get permission groups from API
  async getPermissionGroups(businessId) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.roleBaseURL}/connector/api/permission-groups?business_id=${businessId}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error(`Failed to fetch permission groups: ${response.status}`);
      return await response.json();
    } catch (error) {
      console.error('Error fetching permission groups:', error);
      throw error;
    }
  }

  // Create a new role
  async createRole(roleData) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.roleBaseURL}/connector/api/roles`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(roleData)
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Failed to create role: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error creating role:', error);
      throw error;
    }
  }

  // Update an existing role
  async updateRole(roleId, roleData) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.roleBaseURL}/connector/api/roles/${roleId}`;
      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(roleData)
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Failed to update role: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error updating role:', error);
      throw error;
    }
  }

  // Delete a role
  async deleteRole(roleId) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.roleBaseURL}/connector/api/roles/${roleId}`;
      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Failed to delete role: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error deleting role:', error);
      throw error;
    }
  }

  // Assign role to user
  async assignRoleToUser(userId, roleId, businessId) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.roleBaseURL}/connector/api/users/${userId}/roles`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ business_id: businessId, role_id: roleId })
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Failed to assign role: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error assigning role to user:', error);
      throw error;
    }
  }

  // Check user permission
  async checkUserPermission(userId, permission, businessId) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.roleBaseURL}/connector/api/users/${userId}/check-permission`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ business_id: businessId, permission })
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Failed to check permission: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error checking user permission:', error);
      throw error;
    }
  }

  // Get business locations from API
  async getBusinessLocations() {
    try {
      const token = await this.getAuthToken();
      // Use per_page=-1 to get all records from backend
      const url = `${this.baseURL}/connector/api/business-location`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error(`Failed to fetch business locations: ${response.status}`);
      return await response.json();
    } catch (error) {
      console.error('Error fetching business locations:', error);
      throw error;
    }
  }

  // Import Opening Stock
  async importOpeningStock(fileData) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/opening-stock/import`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: fileData
      });

      if (!response.ok) {
        const rawBody = await response.text();
        let errorText = rawBody;
        try {
          const parsed = JSON.parse(rawBody);
          if (parsed.errors && typeof parsed.errors === 'object') {
            const details = Object.entries(parsed.errors)
              .map(([field, msgs]) => `${field}: ${Array.isArray(msgs) ? msgs.join(', ') : msgs}`)
              .join(' | ');
            errorText = `${parsed.message || 'Validation failed'} → ${details}`;
          } else {
            errorText = parsed.message || rawBody;
          }
        } catch (e) { }
        throw new Error(`Failed to import opening stock: ${response.status} - ${errorText}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error importing opening stock:', error);
      throw error;
    }
  }

  // Download Opening Stock Template
  async downloadOpeningStockTemplate() {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/opening-stock/import-template`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (!response.ok) throw new Error(`Failed to download template: ${response.status}`);
      return await response.blob();
    } catch (error) {
      console.error('Error downloading opening stock template:', error);
      throw error;
    }
  }

  // Get selling price groups from API (Paginated & Filtered)
  async getSellingPriceGroups(params = {}) {
    try {
      const token = await this.getAuthToken();
      let queryUrl = `${this.baseURL}/connector/api/selling-price-groups`;

      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append('page', params.page);
      if (params.per_page) queryParams.append('per_page', params.per_page);
      if (params.search) queryParams.append('search', params.search);
      if (params.is_active !== undefined) queryParams.append('is_active', params.is_active);

      const queryString = queryParams.toString();
      if (queryString) {
        queryUrl += `?${queryString}`;
      }

      const response = await fetch(queryUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error(`Failed to fetch selling price groups: ${response.status}`);
      return await response.json();
    } catch (error) {
      console.error('Error fetching selling price groups:', error);
      throw error;
    }
  }

  // Get active selling price groups
  async getActiveSellingPriceGroups() {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/selling-price-groups/active`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error(`Failed to fetch active selling price groups: ${response.status}`);
      return await response.json();
    } catch (error) {
      console.error('Error fetching active selling price groups:', error);
      throw error;
    }
  }

  // Get specific selling price group
  async getSellingPriceGroup(id) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/selling-price-groups/${id}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error(`Failed to fetch selling price group: ${response.status}`);
      return await response.json();
    } catch (error) {
      console.error('Error fetching selling price group:', error);
      throw error;
    }
  }

  // Create selling price group
  async createSellingPriceGroup(data) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/selling-price-groups`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to create selling price group: ${response.status} - ${errorText}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error creating selling price group:', error);
      throw error;
    }
  }

  // Update selling price group
  async updateSellingPriceGroup(id, data) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/selling-price-groups/${id}`;
      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to update selling price group: ${response.status} - ${errorText}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error updating selling price group:', error);
      throw error;
    }
  }

  // Delete selling price group
  async deleteSellingPriceGroup(id) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/selling-price-groups/${id}`;
      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error(`Failed to delete selling price group: ${response.status}`);
      return await response.json();
    } catch (error) {
      console.error('Error deleting selling price group:', error);
      throw error;
    }
  }

  // Activate/Deactivate Selling Price Group
  async activateDeactivateSellingPriceGroup(id, isActive) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/selling-price-groups/${id}/activate-deactivate`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ is_active: isActive })
      });
      if (!response.ok) throw new Error(`Failed to update selling price group status: ${response.status}`);
      return await response.json();
    } catch (error) {
      console.error('Error updating selling price group status:', error);
      throw error;
    }
  }

  // Toggle Selling Price Group Status
  async toggleSellingPriceGroupStatus(id) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/selling-price-groups/${id}/toggle`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error(`Failed to toggle selling price group status: ${response.status}`);
      return await response.json();
    } catch (error) {
      console.error('Error toggling selling price group status:', error);
      throw error;
    }
  }

  // Get locations for sell page
  async getLocations() {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/business-location?per_page=-1`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch locations: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching locations:', error);
      throw error;
    }
  }

  // Get sales commission agents from API
  async getSalesCommissionAgents() {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/sales-commission-agent?per_page=-1`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error(`Failed to fetch agents: ${response.status}`);
      return await response.json();
    } catch (error) {
      console.error('Error fetching agents:', error);
      throw error;
    }
  }
  // Get categories/taxonomies
  async getCategories() {
    try {
      const token = await this.getAuthToken();
      // Using per_page=-1 to ensure we get all categories if supported, or just default
      const url = `${this.baseURL}/connector/api/taxonomy?per_page=-1&type=product`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch categories: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching categories:', error);
      throw error;
    }
  }

  // Get specific category
  async getCategory(id) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/taxonomy/${id}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch category: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching category:', error);
      throw error;
    }
  }

  // Create a new category
  async createCategory(categoryData) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/taxonomy`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          name: categoryData.name,
          short_code: categoryData.short_code,
          category_type: categoryData.category_type || 'product',
          description: categoryData.description || undefined,
          parent_id: categoryData.parent_id || undefined,
          add_as_sub_cat: categoryData.add_as_sub_cat ? 1 : 0
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errMsg = `Failed to create category: ${response.status}`;
        try { errMsg = JSON.parse(errorText).message || errMsg; } catch (_) { }
        throw new Error(errMsg);
      }
      return await response.json();
    } catch (error) {
      console.error('Error creating category:', error);
      throw error;
    }
  }

  // Update an existing category
  async updateCategory(categoryId, categoryData) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/taxonomy/${categoryId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          name: categoryData.name,
          short_code: categoryData.short_code,
          description: categoryData.description || undefined,
          parent_id: categoryData.parent_id || undefined,
          add_as_sub_cat: categoryData.add_as_sub_cat ? 1 : 0
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errMsg = `Failed to update category: ${response.status}`;
        try { errMsg = JSON.parse(errorText).message || errMsg; } catch (_) { }
        throw new Error(errMsg);
      }
      return await response.json();
    } catch (error) {
      console.error('Error updating category:', error);
      throw error;
    }
  }

  // Delete a category
  async deleteCategory(categoryId) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/taxonomy/${categoryId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errMsg = `Failed to delete category: ${response.status}`;
        try { errMsg = JSON.parse(errorText).message || errMsg; } catch (_) { }
        throw new Error(errMsg);
      }
      return await response.json();
    } catch (error) {
      console.error('Error deleting category:', error);
      throw error;
    }
  }
  // Get units
  async getUnits() {
    try {
      const token = await this.getAuthToken();
      // Using per_page=-1 to ensure we get all units
      const url = `${this.baseURL}/connector/api/unit?per_page=-1`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch units: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching units:', error);
      throw error;
    }
  }

  // Get specific unit
  async getUnit(id) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/unit/${id}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch unit: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching unit:', error);
      throw error;
    }
  }

  // Create a new unit
  async createUnit(unitData) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/unit`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          actual_name: unitData.actual_name,
          short_name: unitData.short_name,
          allow_decimal: unitData.allow_decimal ? 1 : 0,
          base_unit_id: unitData.base_unit_id || undefined,
          base_unit_multiplier: unitData.base_unit_multiplier || undefined
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errMsg = `Failed to create unit: ${response.status}`;
        try { errMsg = JSON.parse(errorText).message || errMsg; } catch (_) { }
        throw new Error(errMsg);
      }
      return await response.json();
    } catch (error) {
      console.error('Error creating unit:', error);
      throw error;
    }
  }

  // Update an existing unit
  async updateUnit(unitId, unitData) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/unit/${unitId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          actual_name: unitData.actual_name,
          short_name: unitData.short_name,
          allow_decimal: unitData.allow_decimal ? 1 : 0,
          base_unit_id: unitData.base_unit_id || undefined,
          base_unit_multiplier: unitData.base_unit_multiplier || undefined
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errMsg = `Failed to update unit: ${response.status}`;
        try { errMsg = JSON.parse(errorText).message || errMsg; } catch (_) { }
        throw new Error(errMsg);
      }
      return await response.json();
    } catch (error) {
      console.error('Error updating unit:', error);
      throw error;
    }
  }

  // Delete a unit
  async deleteUnit(unitId) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/unit/${unitId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errMsg = `Failed to delete unit: ${response.status}`;
        try { errMsg = JSON.parse(errorText).message || errMsg; } catch (_) { }
        throw new Error(errMsg);
      }
      return await response.json();
    } catch (error) {
      console.error('Error deleting unit:', error);
      throw error;
    }
  }
  // Get brands
  async getBrands() {
    try {
      const token = await this.getAuthToken();
      // Using per_page=-1 to ensure we get all brands
      const url = `${this.baseURL}/connector/api/brand?per_page=-1`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch brands: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching brands:', error);
      throw error;
    }
  }

  // Get business details (name, logo, address, etc.)
  async getBusinessDetails() {
    try {
      const token = await this.getAuthToken();

      // Try business-details endpoint first
      try {
        const response = await fetch(`${this.baseURL}/connector/api/business-details`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        if (response.ok) {
          const data = await response.json();
          console.log('Business details response:', data);
          if (data && (data.name || data.data?.name)) {
            return data;
          }
        }
      } catch (e) {
        console.log('business-details endpoint failed, trying fallback');
      }

      // Fallback: get business info from logged-in user endpoint
      const userResponse = await fetch(`${this.baseURL}/connector/api/user/loggedin`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (userResponse.ok) {
        const userData = await userResponse.json();
        console.log('User details for business info:', userData);
        // The user endpoint often includes business details
        if (userData.data?.business || userData.business) {
          return { data: userData.data?.business || userData.business };
        }
        return userData;
      }

      throw new Error('Failed to fetch business details from any endpoint');
    } catch (error) {
      console.error('Error fetching business details:', error);
      throw error;
    }
  }

  // Update shipping details for a sale
  async updateShipping(saleId, shippingData) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/update-shipping-status`;

      // If shippingData is FormData, append id to it (backend expects 'id' not 'sale_id')
      if (shippingData instanceof FormData) {
        shippingData.append('id', saleId);

        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
            // Don't set Content-Type for FormData - let browser set it with boundary
          },
          body: shippingData
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Failed to update shipping: ${response.status} - ${errorText}`);
        }

        const data = await response.json();
        return data;
      } else {
        // Handle JSON data - backend expects 'id' not 'sale_id'
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            id: saleId,
            ...shippingData
          })
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Failed to update shipping: ${response.status} - ${errorText}`);
        }

        const data = await response.json();
        return data;
      }
    } catch (error) {
      console.error('Error updating shipping:', error);
      throw error;
    }
  }

  // Send sale notification (email or SMS)
  async sendSaleNotification(notificationData) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/sell/${notificationData.sale_id}/notification`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(notificationData)
      });

      if (!response.ok) {
        throw new Error(`Failed to send notification: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error sending notification:', error);
      throw error;
    }
  }

  // Send purchase notification (email or SMS)
  async sendPurchaseNotification(notificationData) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/purchases/${notificationData.purchase_id}/notification`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(notificationData)
      });

      if (!response.ok) {
        throw new Error(`Failed to send notification: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error sending purchase notification:', error);
      throw error;
    }
  }

  // ===== EXPENSE MANAGEMENT METHODS =====

  // Get expenses from API with filters
  async getExpenses(params = {}) {
    try {
      const token = await this.getAuthToken();

      const queryParams = new URLSearchParams();
      if (params.page) queryParams.append('page', params.page);
      if (params.per_page) queryParams.append('per_page', params.per_page);
      if (params.search) queryParams.append('search', params.search);
      if (params.location_id && params.location_id !== 'all') queryParams.append('location_id', params.location_id);
      if (params.expense_category_id && params.expense_category_id !== 'all') queryParams.append('expense_category_id', params.expense_category_id);
      if (params.payment_status && params.payment_status !== 'all') queryParams.append('payment_status', params.payment_status);
      if (params.start_date) queryParams.append('start_date', params.start_date);
      if (params.end_date) queryParams.append('end_date', params.end_date);

      const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
      const url = `${this.baseURL}/connector/api/expense${queryString}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch expenses: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching expenses:', error);
      throw error;
    }
  }

  // Create a new expense
  async createExpense(expenseData) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/expense`;

      const formData = new FormData();
      Object.keys(expenseData).forEach(key => {
        if (key === 'document' && expenseData[key]) {
          formData.append(key, expenseData[key]);
        } else if (expenseData[key] !== null && expenseData[key] !== undefined) {
          formData.append(key, expenseData[key]);
        }
      });

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to create expense: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error creating expense:', error);
      throw error;
    }
  }

  // Update an existing expense
  async updateExpense(expenseId, expenseData) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/expense/${expenseId}`;

      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(expenseData)
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to update expense: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error updating expense:', error);
      throw error;
    }
  }

  // Delete an expense
  async deleteExpense(expenseId) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/expense/${expenseId}`;

      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to delete expense: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error deleting expense:', error);
      throw error;
    }
  }

  // Get expense categories
  async getExpenseCategories() {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/expense-categories?per_page=-1`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch expense categories: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching expense categories:', error);
      throw error;
    }
  }

  // Create a new expense category
  async createExpenseCategory(categoryData) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/expense-categories`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(categoryData)
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to create expense category: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error creating expense category:', error);
      throw error;
    }
  }

  // Update an existing expense category
  async updateExpenseCategory(categoryId, categoryData) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/expense-categories/${categoryId}`;

      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(categoryData)
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to update expense category: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error updating expense category:', error);
      throw error;
    }
  }

  // Delete an expense category
  async deleteExpenseCategory(categoryId) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/expense-categories/${categoryId}`;

      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to delete expense category: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error deleting expense category:', error);
      throw error;
    }
  }

  // Get current user profile
  async getCurrentUser() {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/user/loggedin`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch current user: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching current user:', error);
      throw error;
    }
  }

  // Update user profile
  async updateUserProfile(profileData) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/user/update-profile`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: profileData
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to update profile: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error updating profile:', error);
      throw error;
    }
  }

  // Change password
  async changePassword(passwordData) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/user/change-password`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          current_password: passwordData.currentPassword,
          new_password: passwordData.newPassword,
          new_password_confirmation: passwordData.confirmPassword
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to change password: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error changing password:', error);
      throw error;
    }
  }

  // Delete a sell payment line
  async deleteSellPayment(paymentId) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/sell-payment/${paymentId}`;
      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to delete payment: ${response.status} - ${errorText}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error deleting sell payment:', error);
      throw error;
    }
  }

  // Update a sell payment line
  async updateSellPayment(paymentId, paymentData) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/sell-payment/${paymentId}`;
      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(paymentData)
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to update payment: ${response.status} - ${errorText}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error updating sell payment:', error);
      throw error;
    }
  }

  // Add payment to transaction
  async addPayment(paymentData) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/payment-methods/add`;

      console.log('Adding payment with data:', paymentData);

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(paymentData)
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to add payment: ${response.status} - ${errorText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error adding payment:', error);
      throw error;
    }
  }

  // Get single sale details
  async getSale(saleId) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/sell/${saleId}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch sale details: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching sale details:', error);
      throw error;
    }
  }

  // Delete a sale
  async deleteSale(saleId) {
    try {
      const token = await this.getAuthToken();
      const url = `${this.baseURL}/connector/api/sell/${saleId}`;

      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to delete sale: ${response.status} - ${errorText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error deleting sale:', error);
      throw error;
    }
  }

  // ── Stock Adjustment API Methods ──────────────────────────────────────────
  async getStockAdjustments(params = {}) {
    try {
      const token = await this.getAuthToken();
      const queryParams = new URLSearchParams();
      if (params.location_id) queryParams.append('location_id', params.location_id);
      if (params.search) queryParams.append('search', params.search);
      if (params.per_page) queryParams.append('per_page', params.per_page);
      if (params.page) queryParams.append('page', params.page);

      const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
      const response = await fetch(`${this.baseURL}/connector/api/stock-adjustment${queryString}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error(`Failed to fetch stock adjustments: ${response.status}`);
      return await response.json();
    } catch (error) {
      console.error('Error fetching stock adjustments:', error);
      throw error;
    }
  }

  async createStockAdjustment(adjustmentData) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/stock-adjustment`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(adjustmentData)
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to create stock adjustment: ${response.status} - ${errorText}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error creating stock adjustment:', error);
      throw error;
    }
  }

  async getStockAdjustmentDetails(id) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/stock-adjustment/${id}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error(`Failed to fetch adjustment details: ${response.status}`);
      return await response.json();
    } catch (error) {
      console.error('Error fetching adjustment details:', error);
      throw error;
    }
  }

  // ── Import / Export Product & Prices Endpoints ────────────────────────────
  async exportProductPrices() {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/product-prices/export`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' }
      });
      if (!response.ok) {
        const rawBody = await response.text();
        let errorText = rawBody;
        try { errorText = JSON.parse(rawBody).message || rawBody; } catch (e) { }
        throw new Error(`${response.status} - ${errorText}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error exporting prices:', error);
      throw error;
    }
  }

  async downloadProductPrices(downloadUrl) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(downloadUrl, { method: 'GET', headers: { 'Authorization': `Bearer ${token}` } });
      if (!response.ok) throw new Error(`Download failed: ${response.status}`);
      return await response.blob();
    } catch (error) { throw error; }
  }

  async importProductPrices(fileData) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/product-prices/import`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' },
        body: fileData
      });
      if (!response.ok) {
        const rawBody = await response.text();
        let errorText = rawBody;
        try { errorText = JSON.parse(rawBody).message || rawBody; } catch (e) { }
        throw new Error(`${response.status} - ${errorText}`);
      }
      return await response.json();
    } catch (error) { throw error; }
  }

  async getProductPriceGroups() {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/product-prices/price-groups`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' }
      });
      if (!response.ok) throw new Error(`${response.status}`);
      return await response.json();
    } catch (error) { throw error; }
  }

  async downloadImportProductTemplate() {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/products/import-template`, {
        method: 'GET', headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error(`${response.status}`);
      return await response.blob();
    } catch (error) { throw error; }
  }

  async importProducts(fileData) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/products/import`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' },
        body: fileData
      });
      if (!response.ok) {
        const rawBody = await response.text();
        let errorText = rawBody;
        try {
          const parsed = JSON.parse(rawBody);
          if (parsed.errors && typeof parsed.errors === 'object') {
            // Laravel validation errors: { field: ["msg1", "msg2"] }
            const details = Object.entries(parsed.errors)
              .map(([field, msgs]) => `${field}: ${Array.isArray(msgs) ? msgs.join(', ') : msgs}`)
              .join(' | ');
            errorText = `${parsed.message || 'Validation failed'} → ${details}`;
          } else {
            errorText = parsed.message || rawBody;
          }
        } catch (e) { }
        throw new Error(`${response.status} - ${errorText}`);
      }
      return await response.json();
    } catch (error) { throw error; }
  }

  // ── Brand API Methods ──────────────────────────────────────────────────────

  // GET /connector/api/brand  — list all brands
  async getBrands() {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/brand`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error(`Failed to fetch brands: ${response.status}`);
      return await response.json();
    } catch (error) {
      console.error('Error fetching brands:', error);
      throw error;
    }
  }

  // GET /connector/api/brand/{id}  — get single or comma-separated brands
  async getBrand(id) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/brand/${id}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error(`Failed to fetch brand: ${response.status}`);
      return await response.json();
    } catch (error) {
      console.error('Error fetching brand:', error);
      throw error;
    }
  }

  // POST /connector/api/brand  — create brand
  async createBrand(brandData) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/brand`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          name: brandData.name,
          description: brandData.description || '',
          use_for_repair: brandData.use_for_repair ? 1 : 0
        })
      });
      if (!response.ok) {
        const errorText = await response.text();
        let errMsg = `Failed to create brand: ${response.status}`;
        try { errMsg = JSON.parse(errorText).message || errMsg; } catch (_) { }
        throw new Error(errMsg);
      }
      return await response.json();
    } catch (error) {
      console.error('Error creating brand:', error);
      throw error;
    }
  }
  // PUT /connector/api/brand/{id}  — update brand
  async updateBrand(brandId, brandData) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/brand/${brandId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          name: brandData.name,
          description: brandData.description || '',
          use_for_repair: brandData.use_for_repair ? 1 : 0
        })
      });
      if (!response.ok) {
        const errorText = await response.text();
        let errMsg = `Failed to update brand: ${response.status}`;
        try { errMsg = JSON.parse(errorText).message || errMsg; } catch (_) { }
        throw new Error(errMsg);
      }
      return await response.json();
    } catch (error) {
      console.error('Error updating brand:', error);
      throw error;
    }
  }

  // DELETE /connector/api/brand/{id}  — delete brand
  async deleteBrand(brandId) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/brand/${brandId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) {
        const errorText = await response.text();
        let errMsg = `Failed to delete brand: ${response.status}`;
        try { errMsg = JSON.parse(errorText).message || errMsg; } catch (_) { }
        throw new Error(errMsg);
      }
      return await response.json();
    } catch (error) {
      console.error('Error deleting brand:', error);
      throw error;
    }
  }

  // ── Variation API Methods ──────────────────────────────────────────────────

  // GET /connector/api/variations — list all variations
  async getVariations(params = {}) {
    try {
      const token = await this.getAuthToken();
      const queryParams = new URLSearchParams();
      if (params.per_page) queryParams.append('per_page', params.per_page);
      if (params.page) queryParams.append('page', params.page);

      const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
      const response = await fetch(`${this.baseURL}/connector/api/variations${queryString}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error(`Failed to fetch variations: ${response.status}`);
      return await response.json();
    } catch (error) {
      console.error('Error fetching variations:', error);
      throw error;
    }
  }

  // GET /connector/api/variations/{id} — get specific variation
  async getVariation(id) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/variations/${id}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) throw new Error(`Failed to fetch variation: ${response.status}`);
      return await response.json();
    } catch (error) {
      console.error('Error fetching variation:', error);
      throw error;
    }
  }

  // POST /connector/api/variations — create variation
  async createVariation(variationData) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/variations`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(variationData)
      });
      if (!response.ok) {
        const errorText = await response.text();
        let errMsg = `Failed to create variation: ${response.status}`;
        try { errMsg = JSON.parse(errorText).message || errMsg; } catch (_) { }
        throw new Error(errMsg);
      }
      return await response.json();
    } catch (error) {
      console.error('Error creating variation:', error);
      throw error;
    }
  }

  // PUT /connector/api/variations/{id} — update variation
  async updateVariation(id, variationData) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/variations/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(variationData)
      });
      if (!response.ok) {
        const errorText = await response.text();
        let errMsg = `Failed to update variation: ${response.status}`;
        try { errMsg = JSON.parse(errorText).message || errMsg; } catch (_) { }
        throw new Error(errMsg);
      }
      return await response.json();
    } catch (error) {
      console.error('Error updating variation:', error);
      throw error;
    }
  }

  // DELETE /connector/api/variations/{id} — delete variation
  async deleteVariation(id) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/variations/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) {
        const errorText = await response.text();
        let errMsg = `Failed to delete variation: ${response.status}`;
        try {
          const parsed = JSON.parse(errorText);
          errMsg = parsed.message || errMsg;
        } catch (_) { }
        throw new Error(errMsg);
      }
      return await response.json();
    } catch (error) {
      console.error('Error deleting variation:', error);
      throw error;
    }
  }

  // DELETE /connector/api/variations/{id}/values/{value_id} — delete variation value
  async deleteVariationValue(id, valueId) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/variations/${id}/values/${valueId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) {
        const errorText = await response.text();
        let errMsg = `Failed to delete variation value: ${response.status}`;
        try {
          const parsed = JSON.parse(errorText);
          errMsg = parsed.message || errMsg;
        } catch (_) { }
        throw new Error(errMsg);
      }
      return await response.json();
    } catch (error) {
      console.error('Error deleting variation value:', error);
      throw error;
    }
  }

  // ── Stock Report API Methods ──────────────────────────────────────────────────

  // GET /connector/reports/stock/ — list stock report
  async getStockReport(params = {}) {
    try {
      const token = await this.getAuthToken();
      const queryParams = new URLSearchParams();

      // Filter parameters
      if (params.location_id) queryParams.append('location_id', params.location_id);
      if (params.category_id) queryParams.append('category_id', params.category_id);
      if (params.sub_category_id) queryParams.append('sub_category_id', params.sub_category_id);
      if (params.brand_id) queryParams.append('brand_id', params.brand_id);
      if (params.unit_id) queryParams.append('unit_id', params.unit_id);
      if (params.tax_id) queryParams.append('tax_id', params.tax_id);
      if (params.type) queryParams.append('type', params.type);
      if (params.active_state) queryParams.append('active_state', params.active_state);
      if (params.product_id) queryParams.append('product_id', params.product_id);
      if (params.search) {
        queryParams.append('search', params.search);
        queryParams.append('sku', params.search);
        queryParams.append('name', params.search);
        queryParams.append('term', params.search);
      }
      if (params.sku && !params.search) queryParams.append('sku', params.sku);

      // Flags
      if (params.only_mfg_products) queryParams.append('only_mfg_products', '1');
      if (params.not_for_selling) queryParams.append('not_for_selling', '1');

      // Pagination
      if (params.per_page) queryParams.append('per_page', params.per_page);
      if (params.page) queryParams.append('page', params.page);

      const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
      const response = await fetch(`${this.baseURL}/connector/api/reports/stock${queryString}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error(`Failed to fetch stock report: ${response.status}`);
      return await response.json();
    } catch (error) {
      console.error('Error fetching stock report:', error);
      throw error;
    }
  }

  // GET /connector/api/reports/stock/filter-options — get filter options
  async getStockReportFilters() {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/reports/stock/filter-options`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error(`Failed to fetch stock report filters: ${response.status}`);
      return await response.json();
    } catch (error) {
      console.error('Error fetching stock report filters:', error);
      throw error;
    }
  }

  // ── Purchase API Methods ──────────────────────────────────────────────────

  // GET /connector/api/purchasess — list all purchases
  async getPurchases(params = {}) {
    try {
      const token = await this.getAuthToken();
      const queryParams = new URLSearchParams();

      const allowedParams = [
        'location_id', 'supplier_id', 'status', 'payment_status',
        'start_date', 'end_date', 'per_page', 'page', 'search', 'business_id'
      ];

      allowedParams.forEach(key => {
        if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
          queryParams.append(key, params[key]);
        }
      });

      const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
      const response = await fetch(`${this.baseURL}/connector/api/purchases${queryString}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`Purchase API Error (${response.status}):`, errorText);
        throw new Error(`Failed to fetch purchases: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching purchases:', error);
      throw error;
    }
  }

  // GET /connector/api/purchases/{id} — get specific purchase
  async getPurchase(id) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/purchases/${id}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      if (!response.ok) throw new Error(`Failed to fetch purchase: ${response.status}`);
      return await response.json();
    } catch (error) {
      console.error('Error fetching purchase:', error);
      throw error;
    }
  }

  // POST /connector/api/purchases — create purchase
  async createPurchase(purchaseData) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/purchases`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(purchaseData)
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errMsg = `Failed to create purchase: ${response.status}`;
        try {
          const parsed = JSON.parse(errorText);
          if (parsed.errors) {
            const details = Object.values(parsed.errors).flat().join(', ');
            errMsg = `${parsed.message}: ${details}`;
          } else {
            errMsg = parsed.message || errMsg;
          }
        } catch (_) { }
        throw new Error(errMsg);
      }
      return await response.json();
    } catch (error) {
      console.error('Error creating purchase:', error);
      throw error;
    }
  }

  // PUT /connector/api/purchases/{id} — update purchase
  async updatePurchase(id, purchaseData) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/purchases/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(purchaseData)
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errMsg = `Failed to update purchase: ${response.status}`;
        try {
          const parsed = JSON.parse(errorText);
          if (parsed.errors) {
            const details = Object.values(parsed.errors).flat().join(', ');
            errMsg = `${parsed.message}: ${details}`;
          } else {
            errMsg = parsed.message || errMsg;
          }
        } catch (_) { }
        throw new Error(errMsg);
      }
      return await response.json();
    } catch (error) {
      console.error('Error updating purchase:', error);
      throw error;
    }
  }

  // DELETE /connector/api/purchases/{id} — delete purchase
  async deletePurchase(id) {
    try {
      const token = await this.getAuthToken();
      const response = await fetch(`${this.baseURL}/connector/api/purchases/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errMsg = `Failed to delete purchase: ${response.status}`;
        try {
          const parsed = JSON.parse(errorText);
          errMsg = parsed.message || errMsg;
        } catch (_) { }
        throw new Error(errMsg);
      }
      return await response.json();
    } catch (error) {
      console.error('Error deleting purchase:', error);
      throw error;
    }
  }

  // GET /connector/api/purchases/statistics — get purchase statistics
  async getPurchaseStatistics(params = {}) {
    try {
      const token = await this.getAuthToken();
      const queryParams = new URLSearchParams();
      if (params.start_date) queryParams.append('start_date', params.start_date);
      if (params.end_date) queryParams.append('end_date', params.end_date);
      if (params.location_id) queryParams.append('location_id', params.location_id);

      const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
      const response = await fetch(`${this.baseURL}/connector/api/purchases/statistics${queryString}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      if (!response.ok) throw new Error(`Failed to fetch purchase statistics: ${response.status}`);
      return await response.json();
    } catch (error) {
      console.error('Error fetching purchase statistics:', error);
      throw error;
    }
  }
}


// Create singleton instance
const apiService = new ApiService();

export default apiService;
